// Podpis smlouvy na tabletu, strana trenéra (3. 11. 2026, schválený mockup 1/2).
// Trenér ukáže QR kód, tablet ho načte a otevře smlouvu jen pro tuhle klientku. Tady pak vidí,
// jak podpis postupuje (otevřeno na tabletu, SMS, podepsáno). Server: /api/smlouva-podpis.
//
// Props: zkusebniId (průvodce Uzavřít spolupráci) nebo dealId (podpis na prvním tréninku),
//        email (z průvodce), onPodepsano(podpis), onStav(podpis).
import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { QrCode, Tablet, Smartphone, CircleCheck, Clock, RefreshCw, Loader2 } from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import { Oznameni, Pil } from "../sales/salesUi.jsx";
import UdajeSmlouvy from "./UdajeSmlouvy.jsx";

const API = "/api/smlouva-podpis";
async function vol(metoda, data) {
  const r = metoda === "GET"
    ? await apiFetch(`${API}?${new URLSearchParams(data).toString()}`)
    : await apiFetch(API, { method: "POST", body: JSON.stringify(data) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Něco se nepovedlo.");
  return j;
}

function Stav({ hotovo, aktivni, children }) {
  return (
    <div className="flex items-center gap-2.5 text-[14px]">
      {hotovo ? <CircleCheck className="h-5 w-5 shrink-0 text-emerald-600" /> : aktivni ? <Clock className="h-5 w-5 shrink-0 text-brand" /> : <span className="ml-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" />}
      <span className={hotovo ? "font-bold text-slate-900" : aktivni ? "font-bold text-brand-deep" : "text-slate-500"}>{children}</span>
    </div>
  );
}

function zbyva(doIso) {
  const s = Math.max(0, Math.round((new Date(doIso).getTime() - Date.now()) / 1000));
  return { s, text: `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` };
}

export default function PodpisQr({ zkusebniId = null, dealId = null, onPodepsano, onStav, onUdaje }) {
  const klic = zkusebniId ? { zkusebni_id: zkusebniId } : { deal_id: dealId };
  const [podpis, setPodpis] = useState(null);
  const [qr, setQr] = useState(null); // { url, platnost_do, obrazek }
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setTik] = useState(0);
  // 3. 11. 2026: před QR kódem trenér s klientkou zkontroluje údaje do smlouvy (schválený mockup).
  const [kontrolaUdaju, setKontrolaUdaju] = useState(false);
  const posledniUdaje = useRef(null); // potvrzené údaje, posílají se i s každým dalším QR kódem
  const hotovoRef = useRef(false);

  const prijmi = useCallback((p) => {
    setPodpis(p || null);
    onStav?.(p || null);
    if (p && p.stav === "podepsano" && !hotovoRef.current) { hotovoRef.current = true; onPodepsano?.(p); }
  }, [onPodepsano, onStav]);

  const novyQr = useCallback(async (udaje = null) => {
    setBusy(true); setChyba("");
    try {
      const u = udaje || posledniUdaje.current;
      const r = await vol("POST", { akce: "qr", ...klic, ...(u ? { udaje: u } : {}) });
      if (udaje) { posledniUdaje.current = udaje; onUdaje?.(udaje); }
      setKontrolaUdaju(false);
      prijmi(r.podpis);
      if (r.qr) {
        const obrazek = await QRCode.toDataURL(r.qr.url, { margin: 1, width: 520, color: { dark: "#241430", light: "#ffffff" } });
        setQr({ ...r.qr, obrazek });
      } else setQr(null);
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  }, [zkusebniId, dealId, prijmi, onUdaje]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start: když už smlouva běží na tabletu nebo je podepsaná, nový QR se nedělá.
  useEffect(() => {
    let zive = true;
    vol("GET", { akce: "stav", ...klic }).then((r) => {
      if (!zive) return;
      const p = r.podpis;
      if (p && ["podepsano", "tablet", "sms"].includes(p.stav)) prijmi(p);
      else { prijmi(p || { stav: "ceka_tablet" }); setKontrolaUdaju(true); }
    }).catch((e) => zive && setChyba(e.message));
    return () => { zive = false; };
  }, [zkusebniId, dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Průběžný stav (každé 3 s, jen když je stránka vidět) a odpočet platnosti QR.
  useEffect(() => {
    if (podpis?.stav === "podepsano") return undefined;
    const id = setInterval(() => {
      setTik((x) => x + 1);
      if (document.visibilityState !== "visible") return;
      vol("GET", { akce: "stav", ...klic }).then((r) => { if (r.podpis) prijmi(r.podpis); }).catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [podpis?.stav, zkusebniId, dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (chyba) return (
    <div className="space-y-3">
      <Oznameni ton="problem">{chyba}</Oznameni>
      <button type="button" className="btn-secondary" onClick={() => { setChyba(""); if (!podpis) prijmi({ stav: "ceka_tablet" }); setKontrolaUdaju(true); }} disabled={busy}><RefreshCw className="h-4 w-4" />Zkusit znovu</button>
    </div>
  );
  if (kontrolaUdaju) return <UdajeSmlouvy klic={klic} onPotvrzeno={(u) => novyQr(u)} />;
  if (!podpis) return <div className="flex items-center gap-2 py-6 text-[14px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Připravuji smlouvu…</div>;

  if (podpis.stav === "podepsano") return (
    <Oznameni ton="ok">
      <strong>Smlouva je podepsaná{podpis.podepsano ? ` ${podpis.podepsano}` : ""}.</strong>{" "}
      {podpis.rucne ? "Potvrzeno ručně." : podpis.email_odeslan ? `Podepsané PDF odešlo na ${podpis.email}.` : podpis.email_chyba ? "PDF se klientce zatím nepodařilo poslat, Láďa to vyřeší." : "PDF právě odchází klientce e‑mailem."}
    </Oznameni>
  );

  // Podpis na dálku: klientce běží odkaz z SMS a e‑mailu (uzavření po telefonu).
  const qrBezi = qr && new Date(qr.platnost_do).getTime() > Date.now();
  if (podpis.rezim === "dalku" && podpis.stav === "sms" && !podpis.tablet_aktivni && !qrBezi) return (
    <div className="space-y-3 rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Smartphone className="h-5 w-5 text-brand" />Odkaz k podpisu má klientka v telefonu</div>
      <div className="space-y-2.5">
        <Stav hotovo>Odkaz odešel {[!podpis.sms_chyba && "SMS", podpis.dalku_email && "e‑mailem"].filter(Boolean).join(" a ") || "klientce"}{podpis.sms ? ` v ${podpis.sms}` : ""}{podpis.plati_do ? `, platí do ${podpis.plati_do}` : ""}</Stav>
        <Stav hotovo={!!podpis.tel_otevreno} aktivni={!podpis.tel_otevreno}>{podpis.tel_otevreno ? `Klientka odkaz otevřela v ${podpis.tel_otevreno}` : "Klientka ho zatím neotevřela"}</Stav>
        <Stav aktivni={!!podpis.tel_otevreno}>Podepsáno</Stav>
      </div>
      <button type="button" className="btn-secondary" onClick={() => setKontrolaUdaju(true)} disabled={busy}><Tablet className="h-4 w-4" />Podepsat teď na tabletu</button>
    </div>
  );

  // Nový QR kód, který tablet ještě neotevřel, má přednost (i když klientce běží odkaz v SMS).
  const novyNeotevreny = qrBezi && !podpis.tablet_otevreno;
  const tabletSms = podpis.rezim !== "dalku" && podpis.stav === "sms";
  if (!novyNeotevreny && (podpis.stav === "tablet" || tabletSms || podpis.tablet_aktivni)) return (
    <div className="space-y-3 rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Tablet className="h-5 w-5 text-brand" />Smlouva je otevřená na tabletu</div>
      <div className="space-y-2.5">
        <Stav hotovo>Otevřeno na tabletu{podpis.tablet_otevreno ? ` v ${podpis.tablet_otevreno}` : ""}</Stav>
        <Stav hotovo={tabletSms} aktivni={!tabletSms}>Klientka čte smlouvu</Stav>
        <Stav hotovo={tabletSms && !!podpis.tel_otevreno} aktivni={tabletSms && !podpis.tel_otevreno}>{tabletSms && podpis.sms ? `SMS k podpisu odešla v ${podpis.sms}` : "SMS k podpisu do telefonu"}</Stav>
        <Stav aktivni={!!podpis.tel_otevreno}>Podepsáno</Stav>
      </div>
      <Oznameni ton="znacka" ikona={Smartphone}>Až klientka na tabletu klepne na <strong>Poslat k podpisu do telefonu</strong>, přijde jí SMS na {podpis.telefon}. Tady uvidíš, jakmile podepíše.</Oznameni>
      <button type="button" className="btn-secondary" onClick={() => novyQr()} disabled={busy}><RefreshCw className="h-4 w-4" />Otevřít znovu na jiném tabletu</button>
    </div>
  );

  // ceka_tablet: QR kód
  const z = qr ? zbyva(qr.platnost_do) : null;
  const vyprsel = !qr || z.s <= 0;
  return (
    <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><QrCode className="h-5 w-5 text-brand" />Načti QR kód tabletem</div>
        <div className={`rounded-2xl border-2 border-brand-200 bg-white p-2 ${vyprsel ? "opacity-30" : ""}`}>
          {qr ? <img src={qr.obrazek} alt="QR kód pro tablet" className="h-56 w-56 sm:h-60 sm:w-60" /> : <div className="h-56 w-56" />}
        </div>
        <p className="max-w-sm text-[14px] text-slate-600">Na tabletu otevři fotoaparát a namiř ho na kód. Otevře se smlouva <strong>jen pro tuto klientku</strong> ({podpis.jmeno}), tablet se nikam nepřihlašuje.</p>
        <div className="flex flex-wrap justify-center gap-2">
          {vyprsel ? <Pil ton="pozor" ikona={Clock} casti="kód vypršel" /> : <Pil ton="znacka" ikona={Clock} casti={["platí ještě", z.text]} />}
          <Pil ton="neutralni" casti="jednorázový" />
        </div>
        <button type="button" className={vyprsel ? "btn-primary" : "btn-secondary"} onClick={() => novyQr()} disabled={busy}><RefreshCw className="h-4 w-4" />Nový kód</button>
      </div>
    </div>
  );
}
